import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { RolesService } from '../../../shared/services/roles.service';
import { RoleDTO } from '../../../shared/types/role.types';
import {
  RoleFormComponent,
  RoleFormPayload,
} from '../role-form/role-form.component';

@Component({
  selector: 'app-role-edit',
  imports: [
    CommonModule,
    RouterModule,
    RoleFormComponent,
    PageHeaderComponent,
    DataStateComponent,
  ],
  templateUrl: './role-edit.component.html',
})
export class RoleEditComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly rolesService = inject(RolesService);
  private readonly destroyRef = inject(DestroyRef);

  readonly role = signal<RoleDTO | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly submitting = signal(false);
  readonly serverError = signal<string | null>(null);

  private name = '';

  ngOnInit(): void {
    this.name = this.route.snapshot.paramMap.get('name') ?? '';
    if (!this.name) {
      this.loadError.set('Nom de rôle invalide.');
      this.loading.set(false);
      return;
    }
    this.fetch();
  }

  fetch(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.rolesService
      .get(this.name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => {
          this.role.set(r);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.loadError.set(
            toErrorMessage(err, 'Impossible de charger ce rôle.'),
          );
        },
      });
  }

  onSave(payload: RoleFormPayload): void {
    this.submitting.set(true);
    this.serverError.set(null);
    this.rolesService
      .update(this.name, { ...payload, name: this.name })
      .subscribe({
        next: () => {
          this.submitting.set(false);
          void this.router.navigate(['/roles', this.name]);
        },
        error: (err: unknown) => {
          this.submitting.set(false);
          this.serverError.set(toErrorMessage(err, 'errors.updateFailed'));
        },
      });
  }

  onCancel(): void { void this.router.navigate(['/roles', this.name]); }
}
